import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { TrendingUp, HelpCircle, Sparkles, Loader2, AlertTriangle, GitBranch, Search } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

const FORECAST_METHODS = [
  {
    key: 'linear',
    label: 'Linear Projection',
    description: 'Extends a straight-line trend into the future using least-squares regression.',
    when: 'Best for data with a steady, consistent trend and no seasonality. Simple and interpretable.',
    example: 'Revenue growing at a constant rate month-over-month.',
  },
  {
    key: 'exponential',
    label: 'Exponential Smoothing (Holt)',
    description: 'Double exponential smoothing that captures both level and trend. Gives more weight to recent data points.',
    when: 'Best when the trend is changing over time. Adapts faster than linear regression to recent shifts.',
    example: 'Sales that were flat but recently started growing.',
  },
  {
    key: 'seasonal_naive',
    label: 'Seasonal Naive',
    description: 'Simply repeats the last complete seasonal cycle.',
    when: 'Best as a baseline for seasonal data.',
    example: 'Retail sales that spike every December.',
  },
  {
    key: 'auto_ets',
    label: 'AutoETS (Holt-Winters)',
    description: 'Triple exponential smoothing with Level, Trend, and Seasonality components.',
    when: 'Best for data with both a trend AND seasonal pattern.',
    example: 'Monthly revenue with year-over-year growth and consistent Q4 peaks.',
  },
  {
    key: 'auto_arima',
    label: 'AutoARIMA',
    description: 'Auto-Regressive Integrated Moving Average. Handles momentum and reversion patterns.',
    when: 'Best for noisy or mean-reverting data.',
    example: 'Stock-like metrics that fluctuate around a level.',
  },
  {
    key: 'prophet',
    label: 'Prophet',
    description: 'Piecewise linear trend + Fourier-based seasonality. Robust to missing data and outliers.',
    when: 'Best for business data with strong seasonal effects and multiple years of history.',
    example: 'Daily website traffic with weekly patterns and yearly seasonality.',
  },
  {
    key: 'moving_average',
    label: 'Moving Average',
    description: 'Projects the average of the last few periods as a flat line.',
    when: 'Best as a simple, stable baseline for noisy data with no clear trend.',
    example: 'Weekly support tickets that hover around a steady level.',
  },
  {
    key: 'weighted_ma',
    label: 'Weighted Moving Average',
    description: 'Like a moving average but gives more weight to the most recent periods.',
    when: 'Best when recent observations matter more but you still want smoothing.',
    example: 'Demand that shifted recently but is otherwise flat.',
  },
  {
    key: 'damped_trend',
    label: 'Damped Trend',
    description: "Holt's smoothing with a damping factor so the trend flattens over time.",
    when: 'Best when a trend exists but shouldn\u2019t be extrapolated indefinitely.',
    example: 'Fast early growth that is expected to taper off.',
  },
  {
    key: 'drift',
    label: 'Drift (Random Walk)',
    description: 'Extends the last value along the average per-period change across history.',
    when: 'Best as a trend-aware baseline for non-seasonal series.',
    example: 'A metric that drifts steadily up or down with random noise.',
  },
  {
    key: 'quadratic',
    label: 'Quadratic Trend',
    description: 'Fits a degree-2 polynomial (curved trend) via least squares.',
    when: 'Best when growth is accelerating or decelerating rather than linear.',
    example: 'Adoption curves that bend upward over time.',
  },
  {
    key: 'hw_multiplicative',
    label: 'Holt-Winters (Multiplicative)',
    description: 'Triple exponential smoothing where seasonal swings scale with the level.',
    when: 'Best for seasonal data whose peaks grow as the overall level grows.',
    example: 'Sales where December spikes get bigger every year.',
  },
  {
    key: 'theta',
    label: 'Theta Method',
    description: 'Combines a smoothed level with half the long-term linear trend (M3 competition winner).',
    when: 'Best as a strong, robust general-purpose forecaster.',
    example: 'Monthly KPIs where you want accuracy without tuning.',
  },
];

export function AnalyticsPanel({ vizConfig, onConfigChange, data, rowDims, valueMeasures, allFields, onPinCorrelation }) {
  const cfg = vizConfig || {};
  const set = (k, v) => onConfigChange({ [k]: v });
  const [helpOpen, setHelpOpen] = useState(false);
  const [aiSuggesting, setAiSuggesting] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [anomalyLoading, setAnomalyLoading] = useState(false);
  const [anomalyResult, setAnomalyResult] = useState(null);
  const [anomalyOpen, setAnomalyOpen] = useState(false);
  const [corrLoading, setCorrLoading] = useState(false);
  const [corrResult, setCorrResult] = useState(null);
  const [corrOpen, setCorrOpen] = useState(false);
  const [clusterLoading, setClusterLoading] = useState(false);
  const [clusterResult, setClusterResult] = useState(null);
  const [clusterOpen, setClusterOpen] = useState(false);

  const handleAiSuggest = async () => {
    if (!data?.length || !valueMeasures?.length) {
      toast.error('Need data with at least one measure to analyze');
      return;
    }
    setAiSuggesting(true);
    try {
      const res = await api.suggestForecastMethod({
        data: data.slice(0, 50),
        dimension: rowDims?.[0] || '',
        measure: valueMeasures[0],
      });
      setAiSuggestion(res.suggestion);
      setAiDialogOpen(true);
    } catch (err) {
      toast.error(err.message || 'AI suggestion failed');
    } finally {
      setAiSuggesting(false);
    }
  };

  const applyAiSuggestion = () => {
    if (aiSuggestion?.method) {
      onConfigChange({ forecast: true, forecastMethod: aiSuggestion.method });
      toast.success(`Applied: ${FORECAST_METHODS.find(m => m.key === aiSuggestion.method)?.label || aiSuggestion.method}`);
      setAiDialogOpen(false);
    }
  };

  const handleAnomalyDetect = async () => {
    if (!data?.length || !valueMeasures?.length) { toast.error('Need data to analyze'); return; }
    setAnomalyLoading(true);
    try {
      const res = await api.detectAnomalies({ data: data.slice(0, 60), dimension: rowDims?.[0] || '', measures: valueMeasures });
      setAnomalyResult(res.result);
      setAnomalyOpen(true);
    } catch (err) { toast.error(err.message || 'Anomaly detection failed'); }
    finally { setAnomalyLoading(false); }
  };

  const handleCorrelation = async () => {
    if (!data?.length || !valueMeasures?.length) { toast.error('Need data to analyze'); return; }
    setCorrLoading(true);
    try {
      const dims = allFields?.filter(f => f.semantic_type === 'dimension').map(f => f.name) || rowDims || [];
      const measures = allFields?.filter(f => f.semantic_type === 'measure').map(f => f.name) || valueMeasures || [];
      const res = await api.correlationAnalysis({ data: data.slice(0, 60), target_measure: valueMeasures[0], dimensions: dims, measures });
      setCorrResult(res.result);
      setCorrOpen(true);
    } catch (err) { toast.error(err.message || 'Correlation analysis failed'); }
    finally { setCorrLoading(false); }
  };

  const handleClustering = async () => {
    if (!data?.length || !valueMeasures?.length) { toast.error('Need data to analyze'); return; }
    setClusterLoading(true);
    try {
      const dims = rowDims || [];
      const res = await api.clusteringAnalysis({ data: data.slice(0, 60), dimensions: dims, measures: valueMeasures });
      setClusterResult(res.result);
      setClusterOpen(true);
    } catch (err) { toast.error(err.message || 'Clustering failed'); }
    finally { setClusterLoading(false); }
  };

  return (
    <div className="p-3 border-b border-border/30 space-y-1.5">
      <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold flex items-center gap-1"><TrendingUp className="w-2.5 h-2.5" /> Analytics</p>

      {/* Trend Line */}
      <div className="flex items-center justify-between gap-2"><Label className="text-[10px] text-muted-foreground truncate min-w-0">Trend Line</Label><Switch className="flex-shrink-0" checked={cfg.trendLine || false} onCheckedChange={v => set('trendLine', v)} /></div>
      {cfg.trendLine && (
        <Select value={cfg.trendType || 'linear'} onValueChange={v => set('trendType', v)}>
          <SelectTrigger className="!h-6 !text-[9px] !rounded !bg-muted/30 !border-border/30 mt-0.5"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="linear">Linear</SelectItem><SelectItem value="polynomial">Polynomial</SelectItem><SelectItem value="exponential">Exponential</SelectItem></SelectContent>
        </Select>
      )}

      {/* Moving Average */}
      <div className="flex items-center justify-between gap-2"><Label className="text-[10px] text-muted-foreground truncate min-w-0">Moving Average</Label><Switch className="flex-shrink-0" checked={cfg.movingAvg || false} onCheckedChange={v => set('movingAvg', v)} /></div>
      {cfg.movingAvg && (
        <div className="flex items-center gap-1 mt-0.5">
          <Label className="text-[8px] text-muted-foreground">Window</Label>
          <Input value={cfg.movingAvgWindow || 3} onChange={e => set('movingAvgWindow', Number(e.target.value))} type="number" min="2" max="20" className="!h-6 !text-[9px] !rounded !bg-muted/30 !border-border/30 w-14" />
        </div>
      )}

      {/* Statistical Lines */}
      <div className="flex items-center justify-between gap-2"><Label className="text-[10px] text-muted-foreground truncate min-w-0">Average Line</Label><Switch className="flex-shrink-0" checked={cfg.avgLine || false} onCheckedChange={v => set('avgLine', v)} /></div>
      <div className="flex items-center justify-between gap-2"><Label className="text-[10px] text-muted-foreground truncate min-w-0">Median Line</Label><Switch className="flex-shrink-0" checked={cfg.medianLine || false} onCheckedChange={v => set('medianLine', v)} /></div>
      <div className="flex items-center justify-between gap-2"><Label className="text-[10px] text-muted-foreground truncate min-w-0">Std Dev Bands</Label><Switch className="flex-shrink-0" checked={cfg.stdDevBands || false} onCheckedChange={v => set('stdDevBands', v)} /></div>

      {/* Forecasting */}
      <div className="border-t border-border/20 mt-2 pt-2">
        <p className="text-[8px] text-muted-foreground uppercase tracking-wider mb-1">Forecasting</p>
        <div className="flex items-center justify-between gap-2"><Label className="text-[10px] text-muted-foreground truncate min-w-0">Forecast</Label><Switch className="flex-shrink-0" checked={cfg.forecast || false} onCheckedChange={v => set('forecast', v)} /></div>
        {cfg.forecast && (
          <React.Fragment>
            <div className="flex items-center gap-1 mt-0.5">
              <Label className="text-[8px] text-muted-foreground">Periods</Label>
              <Input value={cfg.forecastPeriods || 3} onChange={e => set('forecastPeriods', Number(e.target.value))} type="number" min="1" max="24" className="!h-6 !text-[9px] !rounded !bg-muted/30 !border-border/30 w-14" />
            </div>
            <div>
              <div className="flex items-center gap-1">
                <Label className="text-[8px] text-muted-foreground">Method</Label>
                <button onClick={() => setHelpOpen(true)} className="text-muted-foreground/70 hover:text-primary transition-colors" title="Learn about methods"><HelpCircle className="w-3 h-3" /></button>
                <button onClick={handleAiSuggest} disabled={aiSuggesting} className="ml-auto flex items-center gap-0.5 text-[8px] text-indigo-400 hover:text-indigo-300 transition-colors disabled:opacity-50" title="AI Suggest best method" data-testid="ai-suggest-forecast-btn">
                  {aiSuggesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                  <span>AI Suggest</span>
                </button>
              </div>
              <Select value={cfg.forecastMethod || 'linear'} onValueChange={v => set('forecastMethod', v)}>
                <SelectTrigger className="!h-6 !text-[9px] !rounded !bg-muted/30 !border-border/30 mt-0.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FORECAST_METHODS.map(m => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {(cfg.forecastMethod === 'seasonal_naive' || cfg.forecastMethod === 'auto_ets' || cfg.forecastMethod === 'prophet' || cfg.forecastMethod === 'hw_multiplicative') && (
              <div className="flex items-center gap-1 mt-0.5">
                <Label className="text-[8px] text-muted-foreground">Season Length</Label>
                <Input value={cfg.forecastSeasonLength || 12} onChange={e => set('forecastSeasonLength', Number(e.target.value))} type="number" min="2" max="52" className="!h-6 !text-[9px] !rounded !bg-muted/30 !border-border/30 w-14" />
              </div>
            )}
            <div className="flex items-center justify-between gap-2 mt-0.5"><Label className="text-[10px] text-muted-foreground truncate min-w-0">Confidence Interval</Label><Switch className="flex-shrink-0" checked={cfg.forecastCI !== false} onCheckedChange={v => set('forecastCI', v)} /></div>
          </React.Fragment>
        )}
      </div>

      {/* AI-Powered Analytics */}
      <div className="border-t border-border/20 mt-2 pt-2">
        <p className="text-[8px] text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1"><Sparkles className="w-2.5 h-2.5 text-indigo-400" /> AI Analytics</p>
        <div className="space-y-1">
          <Button variant="outline" size="sm" className="w-full justify-start text-[9px] h-6 rounded-lg gap-1.5" onClick={handleAnomalyDetect} disabled={anomalyLoading} data-testid="ai-anomaly-btn">
            {anomalyLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <AlertTriangle className="w-3 h-3 text-amber-400" />}
            Detect Anomalies
          </Button>
          <Button variant="outline" size="sm" className="w-full justify-start text-[9px] h-6 rounded-lg gap-1.5" onClick={handleCorrelation} disabled={corrLoading} data-testid="ai-correlation-btn">
            {corrLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3 text-emerald-400" />}
            What Drives This?
          </Button>
          <Button variant="outline" size="sm" className="w-full justify-start text-[9px] h-6 rounded-lg gap-1.5" onClick={handleClustering} disabled={clusterLoading} data-testid="ai-clustering-btn">
            {clusterLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <GitBranch className="w-3 h-3 text-violet-400" />}
            Cluster / Segment
          </Button>
        </div>
      </div>

      {/* ── AI Suggestion Dialog ── */}
      <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-md" data-testid="ai-suggest-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Sparkles className="w-4 h-4 text-indigo-400" /> AI Forecast Recommendation</DialogTitle></DialogHeader>
          {aiSuggestion && (
            <div className="space-y-3 mt-2">
              <div className="p-3 rounded-xl bg-indigo-500/10 border border-indigo-500/20">
                <p className="text-sm font-semibold text-foreground">{FORECAST_METHODS.find(m => m.key === aiSuggestion.method)?.label || aiSuggestion.method}</p>
                <p className="text-xs text-foreground/80 mt-1">{aiSuggestion.reasoning}</p>
                {aiSuggestion.confidence && <p className="text-[10px] text-indigo-400 mt-1">Confidence: {aiSuggestion.confidence}%</p>}
              </div>
              {aiSuggestion.data_characteristics && (
                <div className="grid grid-cols-3 gap-2 text-[10px]">
                  <div className="p-2 rounded-lg bg-muted/20"><span className="text-muted-foreground block mb-0.5">Trend</span><span className="text-foreground">{aiSuggestion.data_characteristics.trend}</span></div>
                  <div className="p-2 rounded-lg bg-muted/20"><span className="text-muted-foreground block mb-0.5">Seasonality</span><span className="text-foreground">{aiSuggestion.data_characteristics.seasonality}</span></div>
                  <div className="p-2 rounded-lg bg-muted/20"><span className="text-muted-foreground block mb-0.5">Noise</span><span className="text-foreground">{aiSuggestion.data_characteristics.noise}</span></div>
                </div>
              )}
              {aiSuggestion.alternative && (
                <p className="text-[10px] text-muted-foreground">Alternative: {FORECAST_METHODS.find(m => m.key === aiSuggestion.alternative)?.label || aiSuggestion.alternative}</p>
              )}
              <Button size="sm" className="w-full rounded-lg" onClick={applyAiSuggestion} data-testid="ai-suggest-apply-btn">
                <Sparkles className="w-3 h-3 mr-1" /> Apply {FORECAST_METHODS.find(m => m.key === aiSuggestion.method)?.label}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Anomaly Detection Dialog ── */}
      <Dialog open={anomalyOpen} onOpenChange={setAnomalyOpen}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-lg max-h-[80vh] overflow-auto" data-testid="anomaly-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-400" /> Anomaly Detection</DialogTitle></DialogHeader>
          {anomalyResult && (
            <div className="space-y-3 mt-2">
              <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                <p className="text-xs text-foreground/80">{anomalyResult.summary}</p>
                <p className="text-[10px] text-amber-400 mt-1">{anomalyResult.anomaly_count} anomalies detected</p>
              </div>
              {(anomalyResult.anomalies || []).map((a, i) => (
                <div key={i} className={`p-2.5 rounded-lg border text-[10px] ${a.severity === 'high' ? 'border-red-500/30 bg-red-500/5' : a.severity === 'medium' ? 'border-amber-500/30 bg-amber-500/5' : 'border-border/30'}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-foreground">{a.dimension_value}</span>
                    <span className={`uppercase text-[8px] font-bold ${a.severity === 'high' ? 'text-red-400' : a.severity === 'medium' ? 'text-amber-400' : 'text-muted-foreground'}`}>{a.severity}</span>
                  </div>
                  <p className="text-muted-foreground">{a.measure}: {a.value} (expected: {a.expected_range})</p>
                  <p className="text-foreground/70 mt-0.5">{a.explanation}</p>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Correlation Dialog ── */}
      <Dialog open={corrOpen} onOpenChange={setCorrOpen}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-lg max-h-[80vh] overflow-auto" data-testid="correlation-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Search className="w-4 h-4 text-emerald-400" /> What Drives This Metric?</DialogTitle></DialogHeader>
          {corrResult && (
            <div className="space-y-3 mt-2">
              <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                <p className="text-xs text-foreground/80">{corrResult.summary}</p>
              </div>
              {(corrResult.drivers || []).map((d, i) => (
                <div key={i} className="p-2.5 rounded-lg border border-border/30 text-[10px]">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-foreground">{d.field?.replace(/_/g, ' ')}</span>
                    <span className={`text-[8px] font-bold ${d.correlation?.includes('positive') ? 'text-sky-400' : 'text-rose-400'}`}>{d.correlation}</span>
                  </div>
                  <p className="text-foreground/70">{d.insight}</p>
                </div>
              ))}
              {corrResult.recommendation && (
                <div className="p-2.5 rounded-lg bg-primary/5 border border-primary/20 text-[10px]">
                  <span className="text-primary font-semibold">Recommendation:</span>
                  <span className="text-foreground/80 ml-1">{corrResult.recommendation}</span>
                </div>
              )}
              {onPinCorrelation && (
                <Button variant="outline" size="sm" className="w-full rounded-lg text-xs gap-1.5" onClick={() => { onPinCorrelation(corrResult); setCorrOpen(false); toast.success('Correlation pinned to visual'); }} data-testid="pin-correlation-btn">
                  <Search className="w-3 h-3" /> Pin to Report
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Clustering Dialog ── */}
      <Dialog open={clusterOpen} onOpenChange={setClusterOpen}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-lg max-h-[80vh] overflow-auto" data-testid="clustering-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><GitBranch className="w-4 h-4 text-violet-400" /> Data Segmentation</DialogTitle></DialogHeader>
          {clusterResult && (
            <div className="space-y-3 mt-2">
              <div className="p-3 rounded-xl bg-violet-500/10 border border-violet-500/20">
                <p className="text-xs text-foreground/80">{clusterResult.summary}</p>
              </div>
              {(clusterResult.clusters || []).map((c, i) => (
                <div key={i} className="p-2.5 rounded-lg border border-border/30 text-[10px]">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-foreground">{c.name}</span>
                    <span className="text-muted-foreground">{c.size} members</span>
                  </div>
                  <p className="text-foreground/70 mb-1">{c.description}</p>
                  {c.members && <p className="text-muted-foreground/80 truncate">Members: {c.members.slice(0, 5).join(', ')}{c.members.length > 5 ? '...' : ''}</p>}
                </div>
              ))}
              {clusterResult.business_insight && (
                <div className="p-2.5 rounded-lg bg-primary/5 border border-primary/20 text-[10px]">
                  <span className="text-primary font-semibold">Insight:</span>
                  <span className="text-foreground/80 ml-1">{clusterResult.business_insight}</span>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Forecast Methods Help Dialog */}
      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-lg max-h-[80vh] overflow-auto">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><TrendingUp className="w-4 h-4 text-primary" /> Forecast Methods</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            {FORECAST_METHODS.map(m => (
              <div key={m.key} className={`p-3 rounded-xl border transition-all ${cfg.forecastMethod === m.key ? 'border-primary/40 bg-primary/5' : 'border-border/30'}`}>
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-sm font-semibold text-foreground">{m.label}</h3>
                  {cfg.forecastMethod === m.key && <span className="text-[8px] text-primary font-semibold uppercase">Selected</span>}
                </div>
                <p className="text-xs text-foreground/80 mb-2">{m.description}</p>
                <div className="space-y-1">
                  <div className="flex gap-2"><span className="text-[10px] text-emerald-400 font-semibold flex-shrink-0">When to use:</span><span className="text-[10px] text-muted-foreground">{m.when}</span></div>
                  <div className="flex gap-2"><span className="text-[10px] text-amber-400 font-semibold flex-shrink-0">Example:</span><span className="text-[10px] text-muted-foreground italic">{m.example}</span></div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
